import { useEffect, useState } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

/**
 * Closing Strategy upsell for buyers who own the $49 Inspection Summary.
 *
 * - Renders a calm CTA button at the bottom of the report.
 * - Also shows a small dismissible popup 60s after the report finishes generating,
 *   once per report per browser (localStorage flag).
 *
 * Hidden entirely when:
 *   • ownedTier !== "summary"
 *   • report is the public sample
 *   • report has no upload_id
 */
export default function ClosingStrategyUpsell({
    uploadId,
    ownedTier,
    reportId,
    isSample = false,
}) {
    const [loading, setLoading] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);

    const eligible = !isSample && !!uploadId && ownedTier === "summary";
    const flagKey = `bl-cs-upsell-shown-${reportId}`;

    useEffect(() => {
        if (!eligible) return;
        if (typeof window === "undefined") return;
        if (window.localStorage.getItem(flagKey)) return;
        const t = setTimeout(() => {
            setPopupOpen(true);
            try {
                window.localStorage.setItem(flagKey, "1");
                track("upsell_popup_shown", {
                    product: "closing_upgrade",
                    report_id: reportId,
                });
            } catch { /* non-critical */ }
        }, 60_000);
        return () => clearTimeout(t);
    }, [eligible, flagKey, reportId]);

    if (!eligible) return null;

    const startCheckout = async (source) => {
        try {
            setLoading(true);
            track("upsell_clicked", {
                product: "closing_upgrade",
                source,
                report_id: reportId,
            });
            const r = await createCheckout("upgrade", uploadId);
            window.location.href = r.url;
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not start upgrade. Please try again.",
            );
            setLoading(false);
        }
    };

    return (
        <>
            {/* Inline CTA — anchored at the bottom of the report */}
            <div
                className="bl-card p-6 sm:p-8 md:p-10 border-bl-primary/30 bl-grad-teal-tint-soft"
                data-testid="closing-upgrade-cta"
            >
                <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
                    <div className="flex-1">
                        <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5" /> Closing Strategy upgrade
                        </div>
                        <h3 className="mt-3 font-display font-semibold text-2xl md:text-3xl text-bl tracking-tight leading-tight">
                            Take this to the negotiation table.
                        </h3>
                        <p className="mt-3 text-bl-muted max-w-xl leading-relaxed">
                            Add seller-credit math, ask-language, prioritized concessions,
                            and a downloadable closing strategy memo. Built on the analysis
                            you already have — just{" "}
                            <span className="font-mono-bl text-bl">$50 more</span>.
                        </p>
                    </div>
                    <button
                        onClick={() => startCheckout("inline-cta")}
                        disabled={loading}
                        className="bl-btn-primary whitespace-nowrap"
                        data-testid="closing-upgrade-cta-btn"
                    >
                        {loading ? "Starting…" : (
                            <>
                                Upgrade for $50 <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Subtle floating popup — appears 60s after report load, once per report */}
            {popupOpen && (
                <div
                    className="fixed bottom-5 right-5 z-40 max-w-sm bl-card p-4 shadow-xl border-bl-primary/40 bg-bl-surface"
                    data-testid="closing-upgrade-popup"
                    role="dialog"
                    aria-label="Closing Strategy upgrade suggestion"
                >
                    <button
                        onClick={() => setPopupOpen(false)}
                        className="absolute top-2 right-2 text-bl-muted hover:text-bl p-1"
                        data-testid="closing-upgrade-popup-dismiss"
                        aria-label="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <div className="flex items-start gap-3 pr-5">
                        <div className="w-8 h-8 rounded-md bg-bl-teal-tint flex items-center justify-center text-bl-primary flex-shrink-0">
                            <Sparkles className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-bl text-sm">
                                Ready to negotiate?
                            </div>
                            <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                                Add Closing Strategy for{" "}
                                <span className="font-mono-bl text-bl">$50</span> — seller
                                credit math, ask-language, and a closing memo built on this
                                report.
                            </p>
                            <button
                                onClick={() => startCheckout("popup")}
                                disabled={loading}
                                className="mt-3 bl-btn-primary text-xs py-1.5 px-3"
                                data-testid="closing-upgrade-popup-btn"
                            >
                                {loading ? "Starting…" : "Upgrade for $50"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
